import React, { useState, useEffect } from 'react';
import { Box, Button, Checkbox, FormControlLabel, Typography, Alert, CircularProgress, useMediaQuery, useTheme } from '@mui/material';
import { API_URL } from '../../../utils/apiConfig';

// Use REACT_APP_API_URL_DEVELOPMENT if available, otherwise fallback to REACT_APP_API_URL

export default function AtWillEmploymentForm({ 
  employeeId, 
  formId, 
  onSubmit,
  isCompleted: initialIsCompleted 
}) {
  const [consent, setConsent] = useState(false);
  const [isCompleted, setIsCompleted] = useState(initialIsCompleted);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetchingStatus, setFetchingStatus] = useState(true);
  const [employeeData, setEmployeeData] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const empResponse = await fetch(`${API_URL}/employees/${employeeId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!empResponse.ok) throw new Error('Failed to fetch employee data');
        const empData = await empResponse.json();
        setEmployeeData(empData);
      } catch (err) {
        console.error('Error:', err);
        setError('Failed to load employee data');
      } finally {
        setFetchingStatus(false);
      }
    };

    fetchData();
  }, [employeeId]);

  const handleConsentChange = (event) => {
    setConsent(event.target.checked);
  };

  const handleSubmit = async () => {
    if (!consent) return;

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const submitResponse = await fetch(`${API_URL}/forms/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          formId,
          employeeId,
          formData: {
            consent: true,
            submittedAt: new Date().toISOString()
          }
        })
      });

      if (!submitResponse.ok) throw new Error('Failed to submit form');

      setIsCompleted(true);
      // Pass both formId and filePath (null for non-document forms)
      if (onSubmit) onSubmit(formId, null);
    } catch (err) {
      console.error('Error:', err);
      setError(err.message || 'Failed to submit form');
    } finally {
      setLoading(false);
    }
  };

  if (fetchingStatus) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const companyName = employeeData?.companyName || '[Company Name]';
  const employeeName = employeeData ? `${employeeData.firstName} ${employeeData.lastName}` : '[Employee Name]';

  return (
    <Box sx={{ 
      p: isMobile ? 1.5 : 2, 
      pb: isMobile ? 10 : 2,
      maxWidth: '800px', 
      margin: 'auto', 
      backgroundColor: '#f9f9f9', 
      border: '1px solid #ddd', 
      borderRadius: '4px' 
    }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ mb: 2, fontWeight: 'bold', wordBreak: 'break-word' }}>
        At-Will Employment Agreement
      </Typography>
      <Typography variant={isMobile ? "body2" : "body1"} sx={{ mb: 3 }}>
        This At-Will Employment Agreement (the "Agreement") is made between {companyName} (the "Company") and {employeeName} (the "Employee").
      </Typography>
      <Typography variant={isMobile ? "body2" : "body1"} sx={{ mb: 3 }}>
        By checking the box below, you acknowledge that you have read, understand, and voluntarily agree to this legally binding agreement. You confirm that you are signing this document electronically and that your electronic signature has the same legal effect as a handwritten signature.
      </Typography>
      
      <FormControlLabel
        control={<Checkbox checked={consent} onChange={handleConsentChange} />}
        label="I acknowledge and agree to the above terms."
      />

      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!consent || loading}
        >
          Submit
        </Button>
        {loading && <CircularProgress size={24} />}
      </Box>
    </Box>
  );
}
